export const BILLING_ERROR_CODES = [
	"DECLINED",
	"INSUFFICIENT_FUNDS",
	"EXPIRED_CARD",
	"INVALID_CARD",
	"CVV_MISMATCH",
	"AVS_MISMATCH",
	"CARD_UNUSABLE",
	"SERVICE_UNAVAILABLE",
	"TOO_MANY_ATTEMPTS",
	"GENERIC",
] as const;

export type BillingErrorCode = typeof BILLING_ERROR_CODES[number];

// Copy is Figma's (Billing section 3392:9154); the "can't be used" toast drops the stray closing
// quote the design carries.
export const CARD_ERROR_MESSAGES: Record<BillingErrorCode, string> = {
	DECLINED: "Your card was declined. Please check your card details or try a different payment method.",
	INSUFFICIENT_FUNDS: "Your card was declined due to insufficient funds. Please try a different card.",
	EXPIRED_CARD: "This card has expired. Please check the expiration date or use a different card.",
	INVALID_CARD: "We couldn't verify this card number. Please double-check it and try again.",
	CVV_MISMATCH: "The security code (CVV) doesn't match your card. Please check and try again.",
	AVS_MISMATCH: "The billing address doesn't match what your card issuer has on file. Please double-check your address and try again.",
	CARD_UNUSABLE: "This card can't be used for this transaction. Please contact your card issuer or try a different card.",
	SERVICE_UNAVAILABLE: "We're having trouble processing payments right now. Please try again in a few minutes.",
	// Not drawn in Figma.
	TOO_MANY_ATTEMPTS: "Too many attempts. Please wait and try again later.",
	GENERIC: "We weren't able to process this card. Please contact your card issuer or try a different payment method.",
};

export const CARD_SAVED_MESSAGE = "Changes saved!";

const isBillingErrorCode = (code?: string): code is BillingErrorCode =>
	!!code && (BILLING_ERROR_CODES as readonly string[]).includes(code);

/**
 * Toast copy for a failed card update. A known errorCode wins; otherwise a 5xx or a request that
 * never got a response (no status) is the payment system being down, and anything else, including
 * 409 and unknown codes, gets the generic decline.
 */
export const cardErrorMessage = (error?: { status?: number; errorCode?: string } | null): string => {
	if (isBillingErrorCode(error?.errorCode)) {
		return CARD_ERROR_MESSAGES[error!.errorCode as BillingErrorCode];
	}
	if (error?.errorCode) {
		return CARD_ERROR_MESSAGES.GENERIC;
	}
	const status = error?.status;
	if (status === undefined || status === null || status >= 500) {
		return CARD_ERROR_MESSAGES.SERVICE_UNAVAILABLE;
	}
	return CARD_ERROR_MESSAGES.GENERIC;
};

export type CardField = "number" | "expiry" | "cvv";
export type AddressField = "address1" | "city" | "state" | "zip";

export const ADDRESS_FIELD_LABELS: Record<AddressField, string> = {
	address1: "Address",
	city: "City",
	state: "State",
	zip: "ZIP Code",
};

// The CVV hint says "3- or 4-digit" where Figma says "3-digit", since Amex codes have four.
export const CARD_FIELD_MESSAGES: Record<CardField, string> = {
	number: "Enter a valid card number.",
	expiry: "Enter valid date (MM/YY)",
	cvv: "Enter your 3- or 4-digit security code",
};

export const requiredMessage = (field: AddressField) => `${ADDRESS_FIELD_LABELS[field]} is required.`;

// Recurly reports token validation failures by its own field names.
const TOKEN_FIELD_MAP: Record<string, CardField | AddressField> = {
	number: "number",
	month: "expiry",
	year: "expiry",
	cvv: "cvv",
	address1: "address1",
	city: "city",
	state: "state",
	postal_code: "zip",
};

/** Field messages for a `recurly.token` validation error. Fields we don't render are dropped. */
export const tokenFieldErrors = (fields?: string[]): Partial<Record<CardField | AddressField, string>> => {
	const result: Partial<Record<CardField | AddressField, string>> = {};
	(fields ?? []).forEach((name) => {
		const field = TOKEN_FIELD_MAP[name];
		if (!field) {
			return;
		}
		result[field] = field in CARD_FIELD_MESSAGES
			? CARD_FIELD_MESSAGES[field as CardField]
			: requiredMessage(field as AddressField);
	});
	return result;
};
